import { FieldRatingAndReviews, RatingAndReviewsFormDTO } from './RatingsAndReviewsFormDTO';

export enum FieldType {
    TEXT = 'TEXT',
    NUMBER = 'NUMBER',
    BOOLEAN = 'BOOLEAN',
    SELECT = 'SELECT',
}

export enum RatingAndReviewFieldID {
    AGE = 'age',
    USAGE_DURATION = 'usageDuration',
    TITLE = 'title',
    NICKNAME = 'nickname',
    CONTENT = 'content',
    RATING = 'rating',
    RECOMMENDED = 'recommended',
    ACCEPTED_TERMS_AND_CONDITIONS = 'acceptedTermsAndConditions',
}

type BaseField<T extends FieldType> = {
    id: string;
    type: T;
    isRequired: boolean;
};

export type TextField = BaseField<FieldType.TEXT>;
export type NumberField = BaseField<FieldType.NUMBER>;
export type BooleanField = BaseField<FieldType.BOOLEAN>;

export type SelectOption = {
    id: string;
    label: string;
    value: string;
    selected: boolean;
};

export type SelectField = BaseField<FieldType.SELECT> & {
    options: SelectOption[];
};

export type ProductInfoReview = {
    id: string;
    name: string;
    imageUrl: string;
    brand: {
        name: string;
    };
};

export type FieldRatingAndReviewsForm = {
    titleField: TextField;
    nicknameField: TextField;
    contentField: TextField;
    ratingField: NumberField;
    recommendedField: BooleanField;
    acceptedTermsAndConditionsField: BooleanField;
    ageField: SelectField;
    usageDurationField: SelectField;
};

export type RatingAndReviewsFormValue = {
    product: ProductInfoReview;
    fields: FieldRatingAndReviewsForm;
};

const getFieldById = (fields: FieldRatingAndReviews, id: RatingAndReviewFieldID) => {
    const field = fields.find((f) => f.id === id);

    if (field?.type === FieldType.SELECT) {
        return {
            ...field,
            options: field.options?.map((option) => ({
                id: option.value,
                label: option.label,
                value: option.label,
            })),
        };
    }
    return field;
};

export const mapRatingAndReviewsForm = (dto: RatingAndReviewsFormDTO): RatingAndReviewsFormValue => ({
    product: {
        id: dto.product.id,
        name: dto.product.name,
        imageUrl: dto.product.imageUrl ?? '',
        brand: {
            name: dto.product.brand.name,
        },
    },
    fields: {
        titleField: getFieldById(dto.fields, RatingAndReviewFieldID.TITLE) as TextField,
        nicknameField: getFieldById(dto.fields, RatingAndReviewFieldID.NICKNAME) as TextField,
        contentField: getFieldById(dto.fields, RatingAndReviewFieldID.CONTENT) as TextField,
        ratingField: getFieldById(dto.fields, RatingAndReviewFieldID.RATING) as NumberField,
        recommendedField: getFieldById(dto.fields, RatingAndReviewFieldID.RECOMMENDED) as BooleanField,
        acceptedTermsAndConditionsField: getFieldById(
            dto.fields,
            RatingAndReviewFieldID.ACCEPTED_TERMS_AND_CONDITIONS,
        ) as BooleanField,
        ageField: getFieldById(dto.fields, RatingAndReviewFieldID.AGE) as SelectField,
        usageDurationField: getFieldById(dto.fields, RatingAndReviewFieldID.USAGE_DURATION) as SelectField,
    },
});
